import React, { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabaseClient'

// ── Types ──────────────────────────────────────────────

type Customer = {
  id: string
  email: string
  full_name: string | null
}

type SendCouponEmailModalProps = {
  couponId: string
  couponCode: string
  onClose: () => void
  onSuccess: () => void
  onError: (msg: string) => void
}

// ── Styles ─────────────────────────────────────────────

const styles = {
  overlay: {
    position: 'fixed' as const,
    inset: 0,
    background: 'rgba(0,0,0,0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  } as React.CSSProperties,
  modal: {
    background: 'white',
    borderRadius: '0.75rem',
    padding: '2rem',
    width: '100%',
    maxWidth: '520px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
  } as React.CSSProperties,
  formGroup: { marginBottom: '1rem' } as React.CSSProperties,
  label: {
    display: 'block',
    marginBottom: '0.3rem',
    fontWeight: '500',
    fontSize: '0.9rem',
    color: '#334155',
  } as React.CSSProperties,
  input: {
    width: '100%',
    padding: '0.5rem 0.75rem',
    border: '1px solid #cbd5e1',
    borderRadius: '0.375rem',
    fontSize: '0.9rem',
    boxSizing: 'border-box' as const,
  } as React.CSSProperties,
  select: {
    width: '100%',
    padding: '0.5rem 0.75rem',
    border: '1px solid #cbd5e1',
    borderRadius: '0.375rem',
    fontSize: '0.9rem',
    boxSizing: 'border-box' as const,
    background: 'white',
  } as React.CSSProperties,
  textarea: {
    width: '100%',
    padding: '0.5rem 0.75rem',
    border: '1px solid #cbd5e1',
    borderRadius: '0.375rem',
    fontSize: '0.9rem',
    boxSizing: 'border-box' as const,
    minHeight: '80px',
    resize: 'vertical' as const,
  } as React.CSSProperties,
  btnPrimary: {
    padding: '0.5rem 1rem',
    background: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '0.375rem',
    cursor: 'pointer',
    fontWeight: '500',
    fontSize: '0.9rem',
  } as React.CSSProperties,
  btnSecondary: {
    padding: '0.5rem 1rem',
    background: '#f1f5f9',
    color: '#475569',
    border: '1px solid #e2e8f0',
    borderRadius: '0.375rem',
    cursor: 'pointer',
    fontSize: '0.9rem',
  } as React.CSSProperties,
  error: {
    padding: '0.75rem 1rem',
    background: '#fef2f2',
    color: '#dc2626',
    borderRadius: '0.375rem',
    fontSize: '0.85rem',
    marginBottom: '1rem',
  } as React.CSSProperties,
}

// ── Component ──────────────────────────────────────────

const SendCouponEmailModal: React.FC<SendCouponEmailModalProps> = ({
  couponId,
  couponCode,
  onClose,
  onSuccess,
  onError,
}) => {
  const [email, setEmail] = useState('')
  const [customerName, setCustomerName] = useState('')
  const [customMessage, setCustomMessage] = useState('')
  const [sending, setSending] = useState(false)
  const [localError, setLocalError] = useState<string | null>(null)
  const [customers, setCustomers] = useState<Customer[]>([])
  const [loadingCustomers, setLoadingCustomers] = useState(true)

  // Charger la liste des clients
  useEffect(() => {
    const loadCustomers = async () => {
      setLoadingCustomers(true)
      const { data, error } = await supabase
        .from('profiles')
        .select('id, email, full_name')
        .not('email', 'is', null)
        .order('full_name', { ascending: true })

      if (!error && data) {
        setCustomers(data as Customer[])
      }
      setLoadingCustomers(false)
    }
    loadCustomers()
  }, [])

  // Sélectionner un client dans la liste
  const handleCustomerSelect = (selectedEmail: string) => {
    setEmail(selectedEmail)
    const customer = customers.find((c) => c.email === selectedEmail)
    if (customer?.full_name) {
      setCustomerName(customer.full_name)
    }
    setLocalError(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLocalError(null)

    const trimmedEmail = email.trim()
    if (!trimmedEmail) {
      setLocalError("L'email est obligatoire.")
      return
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setLocalError("L'adresse email n'est pas valide.")
      return
    }

    setSending(true)
    try {
      const emailsEnabled =
        ((import.meta.env.VITE_BOOKING_EMAILS_ENABLED ?? 'true') as string).toLowerCase() !== 'false'

      if (!emailsEnabled) {
        console.log('📧 [dry-run] send-coupon-email désactivé (VITE_BOOKING_EMAILS_ENABLED=false)', {
          couponId,
          email: trimmedEmail,
        })
        onSuccess()
        onClose()
        return
      }

      const { error } = await supabase.functions.invoke('send-coupon-email', {
        body: {
          couponId,
          email: trimmedEmail,
          customerName: customerName.trim() || undefined,
          customMessage: customMessage.trim() || undefined,
        },
      })

      if (error) {
        const msg = error.message || "Erreur lors de l'envoi de l'email."
        setLocalError(msg)
        onError(msg)
      } else {
        onSuccess()
        onClose()
      }
    } catch (err: any) {
      const msg = err?.message || "Erreur inattendue lors de l'envoi."
      setLocalError(msg)
      onError(msg)
    } finally {
      setSending(false)
    }
  }

  // Close on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div style={styles.overlay} onClick={onClose} data-testid="send-coupon-modal-overlay">
      <div
        style={styles.modal}
        onClick={(e) => e.stopPropagation()}
        data-testid="send-coupon-modal"
      >
        <h2 style={{ marginTop: 0, marginBottom: '1.5rem', color: '#1e293b' }}>
          📧 Envoyer le coupon par email
        </h2>

        <p style={{ fontSize: '0.9rem', color: '#64748b', marginBottom: '1rem' }}>
          Coupon : <strong style={{ fontFamily: 'monospace' }}>{couponCode}</strong>
        </p>

        {localError && (
          <div style={styles.error} data-testid="send-email-error">
            {localError}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Sélection client */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Sélectionner un client</label>
            <select
              style={styles.select}
              value={email}
              onChange={(e) => handleCustomerSelect(e.target.value)}
              data-testid="customer-select"
              disabled={loadingCustomers}
            >
              <option value="">
                {loadingCustomers ? 'Chargement…' : '— Choisir un client —'}
              </option>
              {customers.map((c) => (
                <option key={c.id} value={c.email}>
                  {c.full_name ? `${c.full_name} (${c.email})` : c.email}
                </option>
              ))}
            </select>
          </div>

          {/* Email */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Email destinataire *</label>
            <input
              style={styles.input}
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                setLocalError(null)
              }}
              placeholder="email@example.com"
              data-testid="email-input"
              required
            />
          </div>

          {/* Nom client */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Nom du client (optionnel)</label>
            <input
              style={styles.input}
              type="text"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              placeholder="Jean Dupont"
              data-testid="customer-name-input"
            />
          </div>

          {/* Message personnalisé */}
          <div style={styles.formGroup}>
            <label style={styles.label}>Message personnalisé (optionnel)</label>
            <textarea
              style={styles.textarea}
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              placeholder="Profitez de cette offre exclusive..."
              data-testid="custom-message-input"
            />
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
            <button
              type="button"
              style={styles.btnSecondary}
              onClick={onClose}
              disabled={sending}
            >
              Annuler
            </button>
            <button
              type="submit"
              style={{
                ...styles.btnPrimary,
                opacity: sending ? 0.7 : 1,
              }}
              disabled={sending}
              data-testid="send-email-btn"
            >
              {sending ? 'Envoi…' : '📧 Envoyer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default SendCouponEmailModal
